import { CustomProductSubscriptionDto } from "@aihCustomBlueprint/Handlers/AihCustomProductSubscribe/CustomProductSubscriptionDto";
import { getProductNotifications } from "@aihCustomBlueprint/Services/ProductNotificationService";
import { AIHActions } from "@aihCustomBlueprint/Store/CustomApplicationState";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import {
    ApiHandler,
    createHandlerChainRunner,
    executeAwaitableHandlerChain,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";
import {
    GetProductCollectionApiV2Parameter,
    getProductCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductModel, RealTimeInventoryModel } from "@insite/client-framework/Types/ApiModels";
import { DispatchProp } from "react-redux";

export type ProductNotificationDto = {
    ProductModel: ProductModel[];
    ProductInfo: ProductInfo[];
};

type HandlerType = ApiHandler<
    {} & HasOnSuccess<ProductNotificationDto> & HasOnError<string>,
    ServiceResult<CustomProductSubscriptionDto[] | ProductNotificationDto | null>,
    DispatchProp<AIHActions>
>;

export const LoadAllNotificationProducts: HandlerType = async props => {
    props.dispatch({
        type: "Notification/BeginLoad",
    });
    props.apiResult = await getProductNotifications(); // in stock notification
};

export const LoadProductCollection: HandlerType = async props => {
    if (!props.apiResult.successful) {
        return;
    }

    const productDtoResult = props.apiResult.result as CustomProductSubscriptionDto[];

    if (productDtoResult.length <= 0) {
        return;
    }

    const ids = productDtoResult?.map(x => x.ProductId);

    const param: GetProductCollectionApiV2Parameter = {
        productIds: ids,
    };

    const productCollectionResult = await getProductCollectionV2(param);

    props.apiResult.result = {
        ProductModel: productCollectionResult.products,
    } as ProductNotificationDto;
};

const LoadRealTimeInventory: HandlerType = async ({ apiResult, dispatch, getState, parameter }) => {
    if (!apiResult.successful) {
        return;
    }

    if (Array.isArray(apiResult.result) && apiResult.result.length <= 0) {
        return;
    }

    const productNotificationResultDto = apiResult.result as ProductNotificationDto;
    const products = productNotificationResultDto.ProductModel?.filter(p => !!p) as ProductModel[];
    if (!products.length) {
        return;
    }

    const realTimeInventory = await executeAwaitableHandlerChain<
        Parameters<typeof loadRealTimeInventory>[0],
        RealTimeInventoryModel
    >(
        loadRealTimeInventory,
        {
            productIds: products.map(o => o.id),
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    dispatch({
                        type: "Notification/SetFailedToLoadInventory",
                        parameter: { failedToLoadInventory: true },
                    });
                }
            },
        },
        { dispatch, getState },
    );

    productNotificationResultDto.ProductInfo = [];
    productNotificationResultDto.ProductModel.forEach(item => {
        const productInfo = createFromProduct(item);
        const inventory = realTimeInventory.realTimeInventoryResults?.find(x => x.productId === item.id);
        productInfo.inventory = inventory;
        productNotificationResultDto.ProductInfo.push(productInfo);
    });
};

export const LoadRealtimePricing: HandlerType = async props => {
    if (!props.apiResult.successful) {
        return;
    }

    if (Array.isArray(props.apiResult.result) && props.apiResult.result.length <= 0) {
        return;
    }

    const notificationDto = props.apiResult.result as ProductNotificationDto;
    const productPriceParameters = notificationDto.ProductInfo.map(productInfo => {
        return {
            productId: productInfo.productId,
            unitOfMeasure: productInfo.unitOfMeasure,
            qtyOrdered: productInfo.qtyOrdered,
        };
    });

    const promise = new Promise<void>(resolve => {
        props.dispatch(
            loadRealTimePricing({
                productPriceParameters,
                onSuccess: realTimePricing => {
                    realTimePricing.realTimePricingResults?.forEach(pricing => {
                        const productInfoFind = notificationDto.ProductInfo.find(
                            x => x.productId === pricing.productId,
                        );
                        if (productInfoFind) {
                            productInfoFind.pricing = pricing;
                        } else {
                            productInfoFind!.failedToLoadPricing = true;
                        }
                    });
                    resolve();
                },
                onError: () => {
                    notificationDto.ProductInfo.forEach(item => {
                        item.failedToLoadPricing = true;
                    });
                    resolve();
                },
                onComplete(realTimePricingProps) {
                    if (realTimePricingProps.apiResult) {
                        this.onSuccess?.(realTimePricingProps.apiResult);
                    } else if (realTimePricingProps.error) {
                        this.onError?.(realTimePricingProps.error);
                    }
                },
            }),
        );
    });

    await promise;
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult.result as ProductNotificationDto);
    }
};
export const ExecuteOnErrorCallback: HandlerType = props => {
    if (!props.apiResult.successful) {
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const chain = [
    LoadAllNotificationProducts,
    LoadProductCollection,
    LoadRealTimeInventory,
    LoadRealtimePricing,
    ExecuteOnSuccessCallback,
    ExecuteOnErrorCallback,
];

const loadAllNotificationProducts = createHandlerChainRunner(chain, "loadAllNotificationProducts");
export default loadAllNotificationProducts;
